import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowUp, ArrowDown, MousePointer2 } from "lucide-react";

const KEY = "hifireels.desktop.hint.dismissed.v1";

/**
 * DesktopKeyboardHint — one-time bottom-right pill on first desktop visit,
 * teaching the ↑ ↓ arrow keys and mouse wheel navigation. Dismisses via the X
 * button or auto-hides after 8 seconds. Never shown again once dismissed.
 * Hidden on mobile (`md-`).
 */
export default function DesktopKeyboardHint() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Only pop the hint on desktop (>= md breakpoint = 768px) and only once.
    if (typeof window === "undefined") return;
    if (window.matchMedia("(max-width: 767px)").matches) return;
    if (localStorage.getItem(KEY) === "1") return;
    const t = setTimeout(() => setOpen(true), 1500);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => dismiss(), 8000);
    return () => clearTimeout(t);
  }, [open]);

  const dismiss = () => {
    setOpen(false);
    try { localStorage.setItem(KEY, "1"); } catch { /* ignore */ }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="desktop-hint"
          initial={{ opacity: 0, y: 20, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 10, scale: 0.95 }}
          transition={{ type: "spring", stiffness: 260, damping: 22 }}
          className="hidden md:flex fixed bottom-28 right-6 z-[70] max-w-xs bg-gradient-to-br from-[#0B0F19] to-[#1a0f2e] border border-[#00E5FF]/30 rounded-2xl shadow-[0_10px_40px_rgba(0,229,255,0.15)] p-3 items-start gap-3">
          <div className="w-9 h-9 rounded-full bg-[#00E5FF]/15 flex items-center justify-center flex-shrink-0">
            <MousePointer2 className="w-4 h-4 text-[#00E5FF]"/>
          </div>
          <div className="flex-1">
            <div className="font-outfit font-extrabold text-sm text-[#00E5FF]">Navigate reels</div>
            <div className="text-[11px] text-white/80 mt-1 leading-snug">
              Use the <kbd className="px-1 py-0.5 rounded bg-white/10 border border-white/20 text-[10px] inline-flex items-center gap-0.5"><ArrowUp className="w-2.5 h-2.5"/></kbd> and <kbd className="px-1 py-0.5 rounded bg-white/10 border border-white/20 text-[10px] inline-flex items-center gap-0.5"><ArrowDown className="w-2.5 h-2.5"/></kbd> keys, mouse <b className="text-white">wheel</b>, or the on-screen arrows to swipe between reels.
            </div>
          </div>
          <button onClick={dismiss} data-testid="desktop-hint-close"
            className="p-1 rounded-full hover:bg-white/10 flex-shrink-0"
            aria-label="Dismiss hint">
            <X className="w-3.5 h-3.5 text-white/60"/>
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
